import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalContext } from '../../contexts/PortalContext';
import PortalLayout from '../../components/portal/PortalLayout';
import portalApi from '../../services/portalApi';
import { FileText, Loader, Eye, CreditCard, ChevronRight } from 'lucide-react';

const PortalInvoices = () => {
  const navigate = useNavigate();
  const { token, business, isAuthenticated } = usePortalContext();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [filter, setFilter] = useState('all'); // all, paid, unpaid

  // Get brand color for active filter
  const brandColor = business?.brandColor || '#3b82f6';

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/');
      return;
    }

    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await portalApi.getInvoices(token);
      setInvoices(data);
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const filteredInvoices = invoices.filter(invoice => {
    if (filter === 'paid') return invoice.isPaid;
    if (filter === 'unpaid') return !invoice.isPaid;
    return true;
  });

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center py-12">
          <Loader size={48} className="animate-spin" style={{ color: brandColor }} />
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Invoices</h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            {filteredInvoices.length} {filter === 'all' ? 'total' : filter} invoice{filteredInvoices.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {[
            { key: 'all', label: 'All' },
            { key: 'unpaid', label: 'Unpaid' },
            { key: 'paid', label: 'Paid' }
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap"
              style={filter === f.key ? {
                background: `linear-gradient(135deg, ${brandColor}, ${brandColor}dd)`,
                color: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              } : {
                backgroundColor: '#f3f4f6',
                color: '#374151'
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Invoices List */}
        {filteredInvoices.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 sm:p-12 text-center">
            <FileText size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No invoices found</p>
          </div>
        ) : (
          <>
            {/* Mobile: Card Layout */}
            <div className="sm:hidden space-y-3">
              {filteredInvoices.map((invoice) => (
                <div
                  key={invoice.id}
                  onClick={() => navigate(`/portal/invoices/${invoice.id}`)}
                  className="bg-white rounded-xl shadow-sm p-4 active:bg-gray-50 cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-semibold text-gray-900">#{invoice.invoiceNumber}</p>
                      <p className="text-sm text-gray-500">{formatDate(invoice.createdAt)}</p>
                    </div>
                    {invoice.isPaid ? (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Paid
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        Unpaid
                      </span>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3 line-clamp-1">
                    {invoice.description || 'Service'}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <p className="text-lg font-bold text-gray-900">
                      ${invoice.total.toFixed(2)}
                    </p>
                    <div className="flex items-center gap-2">
                      {!invoice.isPaid && (
                        <span 
                          className="text-xs font-medium px-2 py-1 rounded"
                          style={{ backgroundColor: `${brandColor}15`, color: brandColor }}
                        >
                          Pay Now
                        </span>
                      )}
                      <ChevronRight size={20} className="text-gray-400" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: Table Layout */}
            <div className="hidden sm:block bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{invoice.invoiceNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(invoice.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                        {invoice.description || 'Service'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        ${invoice.total.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {invoice.isPaid ? (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            Paid
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            Unpaid
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => navigate(`/portal/invoices/${invoice.id}`)}
                          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                          style={{ color: brandColor }}
                          title="View Invoice"
                        >
                          <Eye size={18} />
                        </button>
                        {!invoice.isPaid && (
                          <button
                            onClick={() => navigate(`/portal/invoices/${invoice.id}`)}
                            className="p-2 rounded-lg hover:bg-green-50 transition-colors text-green-600"
                            title="Pay Invoice"
                          >
                            <CreditCard size={18} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </PortalLayout>
  );
};

export default PortalInvoices;